import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const data = {
    en: {
        title: "Electronic Education System Tasks",
        introduction: "This document outlines the framework and responsibilities related to the Electronic Education Authority. Its primary goal is to facilitate and improve educational processes through digital technologies and ensure equitable access to education for all. It includes a set of tasks, objectives, and values that the Electronic Education Authority is obligated to adhere to and implement.",
        values: [
            {
                title: "Development of E-Learning Systems",
                desc: [
                    "Providing access to e-learning systems for students and teachers.",
                    "Designing and developing web and mobile-based educational platforms.",
                    "Implementing Learning Management Systems (HELP) to facilitate education.",
                ],
            },
            {
                title: "Production of E-Learning Content",
                desc: [
                    "Authoring, compiling, and publishing guidebooks and manuals.",
                    "Preparing educational materials such as videos, podcasts, and slides.",
                    "Using modern tools to create interactive and engaging content.",
                ],
            },
            {
                title: "Enhancing E-Learning Skills",
                desc: [
                    "Conducting workshops, seminars, webinars, and online sessions.",
                    "Providing specialized courses for teachers and students in e-learning.",
                    "Encouraging and supporting active participation in online educational programs.",
                ],
            },
            {
                title: "Development and Improvement of E-Learning",
                desc: [
                    "Developing e-learning systems using modern technologies.",
                    "Improving the quality of e-learning materials through continuous user feedback.",
                    "Enhancing the e-learning skills of students and teachers.",
                ],
            },
            {
                title: "Review of E-Learning Matters",
                desc: [
                    "Reviewing proposals, issues, programs, and policies related to e-learning.",
                    "Analyzing educational needs and providing solutions to address challenges.",
                    "Collaborating with relevant institutions to improve e-learning policies.",
                ],
            },
            {
                title: "Monitoring and Evaluation of E-Learning",
                desc: [
                    "Monitoring e-learning systems to ensure optimal performance.",
                    "Evaluating the effectiveness and quality of educational materials using standard criteria.",
                    "Assessing e-learning needs and aligning programs with current requirements.",
                ],
            },
            {
                title: "Support for E-Learning Research",
                desc: [
                    "Supporting research projects in the field of e-learning.",
                    "Encouraging innovation and providing creative solutions to improve education.",
                    "Collaborating with universities and research centers to advance e-learning.",
                ],
            },
            {
                title: "Development of IT Infrastructure",
                desc: [
                    "Upgrading IT infrastructure to support e-learning.",
                    "Providing necessary equipment and tools for online education access.",
                    "Improving the security and stability of e-learning systems.",
                ],
            },
            {
                title: "Collaboration with International Organizations",
                desc: [
                    "Collaborating with international organizations to exchange experiences and knowledge.",
                    "Participating in global conferences and events related to e-learning.",
                    "Adopting global best practices to improve educational systems.",
                ],
            },
        ],
    },
    pr: {
        title: "لایحه وظایف امریت آموزش الکترونیکی",
        introduction: "این لایحه به منظور تعیین چارچوب و مسئولیت‌های مرتبط با امریت آموزش الکترونیکی طراحی شده است. هدف اصلی آن تسهیل و بهبود فرآیندهای آموزشی از طریق فناوری‌های دیجیتال و ایجاد دسترسی عادلانه به آموزش برای همه افراد است. این لایحه شامل مجموعه‌ای از وظایف، اهداف و ارزش‌هایی است که امریت آموزش الکترونیکی موظف به رعایت و اجرای آن‌ها است.",
        values: [
            {
                title: "ایجاد سیستم های آموزش الکترونیکی",
                desc: [
                    "فراهم سازی زمینه دسترسی محصلین و استادان به سیستم های آموزش الکترونیکی.",
                    "طراحی و توسعه پلتفرم های آموزشی مبتنی بر وب و موبایل.",
                    "پیاده سازی سیستم های مدیریت یادگیری (HELP) برای تسهیل آموزش.",
                ],
            },
            {
                title: "تولید محتوای آموزش الکترونیکی",
                desc: [
                    "تالیف، تدوین و چاپ کتاب های راهنما و رهنمود ها.",
                    "تهیه مواد ممد آموزشی الکترونیکی شامل ویدیوها، پادکست ها و اسلایدها.",
                    "استفاده از ابزارهای مدرن برای تولید محتوای تعاملی و جذاب.",
                ],
            },
            {
                title: "ارتقای مهارت های آموزش الکترونیکی",
                desc: [
                    "تدویر ورکشاپ های آموزشی، سمینار ها، ویبینار ها و جلسات آنلاین.",
                    "ارائه دوره های تخصصی برای اساتید و محصلین در زمینه آموزش الکترونیکی.",
                    "تشویق و حمایت از مشارکت فعال در برنامه های آموزشی آنلاین.",
                ],
            },
            {
                title: "انکشاف و بهبود آموزش الکترونیکی",
                desc: [
                    "انکشاف سیستم های آموزش الکترونیکی با استفاده از تکنولوژی های روز.",
                    "بهبود کیفیت مواد آموزشی الکترونیکی با بازخورد مستمر از کاربران.",
                    "بهبود و افزایش مهارت های آموزش الکترونیکی محصلان و استادان.",
                ],
            },
            {
                title: "بررسی امور مربوط به آموزش الکترونیکی",
                desc: [
                    "بررسی پیشنهادات، مشکلات، برنامه ها و پالیسی های مرتبط به آموزش الکترونیکی.",
                    "تحلیل نیازهای آموزشی و ارائه راهکارهای مناسب برای رفع چالش ها.",
                    "همکاری با نهادهای مرتبط برای بهبود سیاست های آموزش الکترونیکی.",
                ],
            },
            {
                title: "نظارت و ارزیابی آموزش الکترونیکی",
                desc: [
                    "نظارت از سیستم های آموزش الکترونیکی برای اطمینان از عملکرد بهینه.",
                    "ارزیابی مواد آموزشی از موثریت و کیفیت آن با استفاده از معیارهای استاندارد.",
                    "نیاز سنجی آموزش الکترونیکی و تطبیق برنامه ها با نیازهای روز.",
                ],
            },
            {
                title: "حمایت از تحقیقات در آموزش الکترونیکی",
                desc: [
                    "حمایت از پروژه های تحقیقاتی در زمینه آموزش الکترونیکی.",
                    "تشویق نوآوری و ارائه راهکارهای خلاقانه برای بهبود آموزش.",
                    "همکاری با پوهنتون ها و مراکز تحقیقاتی برای پیشبرد آموزش الکترونیکی.",
                ],
            },
            {
                title: "توسعه زیرساخت های فناوری اطلاعات",
                desc: [
                    "ارتقای زیرساخت های فناوری اطلاعات برای پشتیبانی از آموزش الکترونیکی.",
                    "تامین تجهیزات و ابزارهای لازم برای دسترسی به آموزش آنلاین.",
                    "بهبود امنیت و پایداری سیستم های آموزش الکترونیکی.",
                ],
            },
            {
                title: "همکاری با سازمان های بین المللی",
                desc: [
                    "همکاری با سازمان های بین المللی برای تبادل تجربیات و دانش.",
                    "شرکت در کنفرانس ها و رویدادهای جهانی مرتبط با آموزش الکترونیکی.",
                    "استفاده از بهترین روش های جهانی برای بهبود سیستم های آموزشی.",
                ],
            },
        ],
    },
    ps: {
        title: "د برېښنایي زده کړې ",
        introduction: "د دې سند موخه د برېښنایي زده کړې د مسئولیتونو او چوکاټ تعیینول دي. د دې اصلي هدف د ډیجیټل ټیکنالوژۍ له لارې د زده کړې پروسې اسانه کول او د ټولو لپاره د زده کړې عادلانه لاسرسی رامینځته کول دي. دا د هغو دندو، موخو او ارزښتونو یو مجموعه شاملوي چې د برېښنایي زده کړې مسئولیت لري او د دې پلي کول یې ضروري دي.",
        values: [
            {
                title: "د برېښنایي زده کړې سیسټمونو جوړول",
                desc: [
                    "د زده کونکو او استادانو لپاره د برېښنایي زده کړې سیسټمونو ته لاسرسی برابرول.",
                    "د ویب او موبایل پر بنسټ د زده کړې پلیټفارمونو ډیزاین او پراختیا.",
                    "د زده کړې د اسانتیا لپاره د زده کړې مدیریت سیسټمونو (HELP) پلي کول.",
                ],
            },
            {
                title: "د برېښنایي زده کړې مواد تولید",
                desc: [
                    "د لارښود کتابونو او لارښودونو تالیف، تنظیم او خپرول.",
                    "د ویډیوګانو، پوډکاسټونو او سلاډونو په ګډون د زده کړې مواد چمتو کول.",
                    "د متقابل او جذاب مواد د تولید لپاره د عصري وسایلو کارول.",
                ],
            },
            {
                title: "د برېښنایي زده کړې مهارتونو ته وده ورکول",
                desc: [
                    "د زده کړې ورکشاپونو، سمینارونو، ویبینارونو او آنلاین غونډو ترتیبول.",
                    "د برېښنایي زده کړې په برخه کې د استادانو او زده کونکو لپاره تخصصي کورسونه وړاندې کول.",
                    "د آنلاین زده کړې پروګرامونو کې فعاله ګډون ته هڅول او ملاتړ کول.",
                ],
            },
            {
                title: "د برېښنایي زده کړې ته وده او ښه والی",
                desc: [
                    "د عصري ټیکنالوژۍ په کارولو سره د برېښنایي زده کړې سیسټمونو ته وده ورکول.",
                    "د کاروونکو له مستمر بازخورد څخه د برېښنایي زده کړې موادو کیفیت ته ښه والی ورکول.",
                    "د زده کونکو او استادانو د برېښنایي زده کړې مهارتونو ته وده ورکول.",
                ],
            },
            {
                title: "د برېښنایي زده کړې اړوند مسائلو بیاکتنه",
                desc: [
                    "د برېښنایي زده کړې اړوند وړاندیزونو، ستونزو، پروګرامونو او پالیسیو بیاکتنه.",
                    "د زده کړې اړتیاو تحلیل او د ستونزو د حل لپاره مناسب حل لارې وړاندې کول.",
                    "د برېښنایي زده کړې پالیسیو د ښه والي لپاره د اړوند مؤسسو سره همکاری.",
                ],
            },
            {
                title: "د برېښنایي زده کړې نظارت او ارزیابی",
                desc: [
                    "د برېښنایي زده کړې سیسټمونو نظارت د دې لپاره چې د دې فعالیتونه په سمه توګه ترسره شي.",
                    "د معیاري معیارونو په کارولو سره د زده کړې موادو د موثریت او کیفیت ارزیابی.",
                    "د برېښنایي زده کړې اړتیاو ارزیابی او پروګرامونه د اوسنیو اړتیاو سره مطابقت ورکول.",
                ],
            },
            {
                title: "د برېښنایي زده کړې څیړنیز ملاتړ",
                desc: [
                    "د برېښنایي زده کړې په برخه کې د څیړنیزو پروژو ملاتړ.",
                    "د نوآوری هڅول او د زده کړې د ښه والي لپاره خلاقانه حل لارې وړاندې کول.",
                    "د پوهنتونونو او څیړنیزو مرکزونو سره همکاری د برېښنایي زده کړې د پرمختګ لپاره.",
                ],
            },
            {
                title: "د بریښنايي زده کړو زیربناوو ته وده ورکول",
                desc: [
                    "د برېښنایي زده کړې د ملاتړ لپاره بریښنايي زده کړو زیربناوو ته وده ورکول.",
                    "د آنلاین زده کړې لاسرسی لپاره د اړتیا وړ تجهیزاتو او وسایلو برابرول.",
                    "د برېښنایي زده کړې سیسټمونو د امنیت او ثبات ته ښه والی ورکول.",
                ],
            },
            {
                title: "د نړیوالو سازمانونو څخه همکاری",
                desc: [
                    " د تجربو او پوهې د تبادلې لپاره د نړیوالو سازمانونو سره لیدنه",
                    "د برېښنایي زده کړې اړوند  کنفرانسونو کې ګډون.",
                    "د زده کړې سیسټمونو د ښه والي لپاره د نړیوالو غوره سیستونو کتنه.",
                ],
            },
        ],
    },
};

const AboutValues = () => {
    const { i18n } = useTranslation();
    const { title, introduction, values } = data[i18n.language];

    const [ref, inView] = useInView({
        triggerOnce: true, // Trigger the animation only once
        threshold: 0.1, // Trigger when 10% of the element is in view
    });

    return (
        <section className='bg-white rounded-2xl'>
            <motion.div
                ref={ref}
                className="px-6 mx-auto my-32 max-w-7xl sm:my-40 lg:px-8 py-10"
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5 }}
            >
                <div className="max-w-2xl mx-auto lg:mx-0">
                    <motion.h2
                        className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl"
                        initial={{ opacity: 0, x: -50 }}
                        animate={inView ? { opacity: 1, x: 0 } : {}}
                        transition={{ duration: 0.5, delay: 0.2 }}
                    >
                        {title}
                    </motion.h2>
                    <motion.p
                        className='pt-4 text-lg text-justify'
                        initial={{ opacity: 0, x: -50 }}
                        animate={inView ? { opacity: 1, x: 0 } : {}}
                        transition={{ duration: 0.5, delay: 0.4 }}
                    >
                        {introduction}
                    </motion.p>
                </div>
                <motion.dl
                    className="grid max-w-2xl grid-cols-1 mx-auto mt-16 text-base leading-7 gap-x-10 gap-y-16 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:grid-cols-3"
                    initial={{ opacity: 0 }}
                    animate={inView ? { opacity: 1 } : {}}
                    transition={{ duration: 0.5, delay: 0.6 }}
                >
                    {values.map((value, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 20 }}
                            animate={inView ? { opacity: 1, y: 0 } : {}}
                            transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                        >
                            <dt className="font-bold text-xl text-gray-900">{value.title}</dt>
                            <dd className="text-gray-600 text-justify">
                                {Array.isArray(value.desc) ? (
                                    <ul>
                                        {value.desc.map((item, i) => (
                                            <li key={i}>{item}</li>
                                        ))}
                                    </ul>
                                ) : (
                                    value.desc
                                )}
                            </dd>
                        </motion.div>
                    ))}
                </motion.dl>
            </motion.div>
        </section>
    );
};

export default AboutValues;